import { useQuery } from "@tanstack/react-query";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { getSubscription, type WorkspaceSubscription } from "@/services/billing.service";

export type { WorkspaceSubscription };

/**
 * Statuses that still entitle the workspace to Pro features. `past_due` stays
 * entitled while Stripe retries the card — matches stripe-webhook's
 * ENTITLED_STATUSES (a downgrade only happens on canceled/unpaid/expired).
 */
const ENTITLED_STATUSES = ["active", "trialing", "past_due"];

export const isSubscriptionEntitled = (subscription?: WorkspaceSubscription | null) =>
  !!subscription && ENTITLED_STATUSES.includes(subscription.status);

/**
 * The workspace's latest plan whatever its status — a `past_due` or `canceled`
 * one still has to be shown and acted on — from the `billing` function, which
 * any member may read. Null when it never had one. Use `isSubscriptionEntitled`
 * to gate Pro features.
 */
export const useWorkspaceSubscription = () => {
  const { currentWorkspaceId } = useWorkspace();

  return useQuery({
    queryKey: ['workspace-subscription', currentWorkspaceId],
    queryFn: () => getSubscription(currentWorkspaceId as string),
    enabled: !!currentWorkspaceId,
  });
};

export const useCampaignAccess = () => {
  const { data: subscription, isLoading, error } = useWorkspaceSubscription();

  return {
    hasAccess: isSubscriptionEntitled(subscription) && subscription.campaign_access === true,
    isLoading,
    error,
    subscription,
  };
};
